// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DashboardBuilder
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS dashboard builder that composes
 * heterogeneous analytics widgets into a page-ready grid. The builder consumes
 * the shared dashboard layout vocabulary so consumers can declare dashboards as
 * data.
 */

import type {
  CohortDashboardWidget,
  ConsentSettings,
  DashboardLayout,
  DashboardWidget,
  DashboardWidgetBase,
  FunnelResult,
  RealtimeStats,
  ReplayDashboardWidget,
  TimelineEvent,
} from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@geenius/ui/solidjs";
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
} from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";
import { ChartCard } from "./ChartCard";
import { ConsentBanner } from "./ConsentBanner";
import { DeviceBreakdown } from "./DeviceBreakdown";
import { EventTable } from "./EventTable";
import { FunnelChart } from "./FunnelChart";
import { RealtimeBadge } from "./RealtimeBadge";
import { StatsCard } from "./StatsCard";
import { Timeline } from "./Timeline";
import { TopPagesTable } from "./TopPagesTable";

type AnalyticsCopy = ReturnType<typeof createAnalyticsCopy>;

interface FunnelDashboardWidget extends Omit<DashboardWidgetBase, "kind"> {
  kind: "funnel";
  result: FunnelResult;
}

interface RealtimeDashboardWidget extends Omit<DashboardWidgetBase, "kind"> {
  kind: "realtime";
  stats: RealtimeStats | null;
  isLoading?: boolean;
}

interface ConsentDashboardWidget extends Omit<DashboardWidgetBase, "kind"> {
  kind: "consent";
  settings?: ConsentSettings | null;
  onAccept?: (settings: ConsentSettings) => void;
  onReject?: (settings: ConsentSettings) => void;
}

interface TimelineDashboardWidget extends Omit<DashboardWidgetBase, "kind"> {
  kind: "timeline";
  events: TimelineEvent[];
  maxVisible?: number;
}

type SolidDashboardWidget =
  | DashboardWidget
  | FunnelDashboardWidget
  | RealtimeDashboardWidget
  | ConsentDashboardWidget
  | TimelineDashboardWidget;

type SolidDashboardLayout = Omit<DashboardLayout, "widgets"> & {
  widgets: SolidDashboardWidget[];
};

/** Time range option shown by the SolidJS dashboard builder toolbar. */
export interface DashboardBuilderTimeRangeOption {
  value: string;
  label: string;
}

const DEFAULT_TIME_RANGE_OPTIONS: DashboardBuilderTimeRangeOption[] = [
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

/**
 * Props for rendering the SolidJS dashboard builder.
 *
 * @property layout Shared dashboard layout definition or a raw widget array.
 * @property editable Whether to render dashboard editing and reordering controls.
 * @property timeRange Controlled selected dashboard time range.
 * @property timeRangeOptions Options shown in the dashboard time-range control.
 * @property selectedWidgetId Controlled selected widget identifier for the editor.
 * @property onLayoutChange Called with the next widget order after reordering.
 * @property onTimeRangeChange Called when the user selects a new time range.
 * @property onWidgetSelect Called when the user opens a widget in the editor.
 * @property class Optional class name merged into the section wrapper.
 */
export interface DashboardBuilderProps {
  layout: DashboardLayout | SolidDashboardLayout | SolidDashboardWidget[];
  editable?: boolean;
  timeRange?: string;
  timeRangeOptions?: DashboardBuilderTimeRangeOption[];
  selectedWidgetId?: string;
  onLayoutChange?: (widgets: SolidDashboardWidget[]) => void;
  onTimeRangeChange?: (timeRange: string) => void;
  onWidgetSelect?: (widget: SolidDashboardWidget) => void;
  class?: string;
}

function moveWidget(
  widgets: SolidDashboardWidget[],
  widgetId: string,
  offset: number,
): SolidDashboardWidget[] {
  const fromIndex = widgets.findIndex((widget) => widget.id === widgetId);
  if (fromIndex < 0) return widgets;

  const toIndex = Math.max(0, Math.min(widgets.length - 1, fromIndex + offset));
  if (fromIndex === toIndex) return widgets;

  const next = [...widgets];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return widgets;
  next.splice(toIndex, 0, moved);
  return next;
}

function moveWidgetToTarget(
  widgets: SolidDashboardWidget[],
  draggedWidgetId: string,
  targetWidgetId: string,
): SolidDashboardWidget[] {
  const fromIndex = widgets.findIndex(
    (widget) => widget.id === draggedWidgetId,
  );
  const toIndex = widgets.findIndex((widget) => widget.id === targetWidgetId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return widgets;

  const next = [...widgets];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return widgets;
  next.splice(toIndex, 0, moved);
  return next;
}

function renderCohort(widget: CohortDashboardWidget, copy: AnalyticsCopy) {
  return (
    <Card data-analytics-dashboard-widget data-widget-kind="cohort">
      <CardHeader class="flex flex-row flex-wrap items-start justify-between gap-3 pb-0">
        <div class="space-y-1">
          <CardTitle class="text-sm" data-analytics-dashboard-widget-title>
            {widget.title}
          </CardTitle>
          <CardDescription>
            {copy.plural(
              "analytics.dashboardBuilder.cohortDescription",
              widget.analysis.intervals.length,
            )}
          </CardDescription>
        </div>
        <div
          class="flex flex-wrap items-center gap-2"
          data-analytics-cohort-overview
        >
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.cohorts",
              widget.analysis.cohorts.length,
            )}
          </Badge>
          <Badge variant="secondary" data-analytics-cohort-intervals>
            {copy.t("analytics.dashboardBuilder.dayIntervals", {
              intervals: widget.analysis.intervals.join(", "),
            })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent
        class="grid gap-3 pt-4 md:grid-cols-2"
        data-analytics-cohort-grid
      >
        <For each={widget.analysis.cohorts}>
          {(cohort) => (
            <Card
              class="border-muted/80 bg-muted/30 shadow-none"
              data-analytics-cohort-card
            >
              <CardContent class="grid gap-3 pt-6">
                <div class="flex items-center justify-between gap-3">
                  <strong class="text-sm font-semibold text-foreground">
                    {cohort.cohort}
                  </strong>
                  <Badge variant="secondary">
                    {copy.plural(
                      "analytics.dashboardBuilder.users",
                      cohort.size,
                    )}
                  </Badge>
                </div>
                <div
                  class="flex flex-col gap-2"
                  data-analytics-cohort-retention
                >
                  <For each={cohort.retained}>
                    {(point) => (
                      <div
                        class="flex items-center justify-between gap-3 rounded-lg bg-background px-3 py-2 text-xs text-muted-foreground"
                        data-analytics-cohort-point
                      >
                        <span class="font-medium text-foreground">
                          {copy.t("analytics.dashboardBuilder.dayLabel", {
                            day: point.day,
                          })}
                        </span>
                        <span>{point.retentionRate.toFixed(1)}%</span>
                      </div>
                    )}
                  </For>
                </div>
              </CardContent>
            </Card>
          )}
        </For>
      </CardContent>
    </Card>
  );
}

function renderReplay(widget: ReplayDashboardWidget, copy: AnalyticsCopy) {
  return (
    <Card data-analytics-dashboard-widget data-widget-kind="replay">
      <CardHeader class="flex flex-row flex-wrap items-start justify-between gap-3 pb-0">
        <div class="space-y-1">
          <CardTitle class="text-sm" data-analytics-dashboard-widget-title>
            {widget.title}
          </CardTitle>
          <CardDescription>
            {copy.t("analytics.dashboardBuilder.replayDescription")}
          </CardDescription>
        </div>
        <div class="flex items-center gap-2">
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.frames",
              widget.replay.frames.length,
            )}
          </Badge>
          <Badge variant="secondary">
            {copy.t("analytics.dashboardBuilder.durationSeconds", {
              seconds: Math.round(widget.replay.duration / 1000),
            })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent class="pt-4">
        <ol class="grid gap-3" data-analytics-session-replay-list>
          <For each={widget.replay.frames}>
            {(frame) => (
              <li
                class="grid gap-2 rounded-xl border border-border bg-muted/40 p-4 text-sm"
                data-analytics-session-replay-frame
              >
                <div class="flex items-center justify-between gap-3">
                  <strong class="text-foreground">{frame.label}</strong>
                  <span class="text-xs text-muted-foreground">
                    {copy.formatTime(frame.timestamp)}
                  </span>
                </div>
                <span class="truncate text-xs text-muted-foreground">
                  {frame.url}
                </span>
              </li>
            )}
          </For>
        </ol>
      </CardContent>
    </Card>
  );
}

function renderConsent(widget: ConsentDashboardWidget) {
  return (
    <Show
      when={widget.onAccept && widget.onReject}
      fallback={
        <Card data-analytics-dashboard-widget data-widget-kind="consent">
          <CardHeader class="pb-0">
            <CardTitle class="text-sm" data-analytics-dashboard-widget-title>
              {widget.title}
            </CardTitle>
          </CardHeader>
          <CardContent class="grid gap-2 pt-4 text-sm text-muted-foreground">
            <span>
              Analytics: {widget.settings?.analytics ? "enabled" : "disabled"}
            </span>
            <span>
              Marketing: {widget.settings?.marketing ? "enabled" : "disabled"}
            </span>
            <span>
              Personalization:{" "}
              {widget.settings?.personalization ? "enabled" : "disabled"}
            </span>
          </CardContent>
        </Card>
      }
    >
      <ConsentBanner
        show
        onAccept={(settings) => widget.onAccept?.(settings)}
        onReject={(settings) => widget.onReject?.(settings)}
      />
    </Show>
  );
}

function renderWidget(widget: SolidDashboardWidget, copy: AnalyticsCopy) {
  switch (widget.kind) {
    case "metric":
      return (
        <StatsCard
          label={widget.title}
          value={widget.metric.value}
          change={
            widget.metric.changePercent !== undefined
              ? `${widget.metric.changePercent.toFixed(1)}%`
              : undefined
          }
          trend={
            widget.metric.trend === "flat" ? "neutral" : widget.metric.trend
          }
        />
      );
    case "chart":
      return (
        <ChartCard
          title={widget.title}
          data={widget.data}
          type={widget.chartType}
        />
      );
    case "top-pages":
      return <TopPagesTable pages={widget.pages} />;
    case "device-breakdown":
      return <DeviceBreakdown breakdown={widget.breakdown} />;
    case "events":
      return <EventTable events={widget.events} maxRows={widget.maxRows} />;
    case "cohort":
      return renderCohort(widget, copy);
    case "replay":
      return renderReplay(widget, copy);
    case "funnel":
      return <FunnelChart result={widget.result} />;
    case "realtime":
      return (
        <Card data-analytics-dashboard-widget data-widget-kind="realtime">
          <CardHeader class="pb-0">
            <CardTitle class="text-sm" data-analytics-dashboard-widget-title>
              {widget.title}
            </CardTitle>
          </CardHeader>
          <CardContent class="pt-4">
            <RealtimeBadge
              stats={widget.stats}
              isLoading={widget.isLoading ?? false}
            />
          </CardContent>
        </Card>
      );
    case "consent":
      return renderConsent(widget);
    case "timeline":
      return <Timeline events={widget.events} maxVisible={widget.maxVisible} />;
    default:
      return null;
  }
}

/**
 * Renders the SolidJS dashboard builder from a shared layout definition.
 *
 * @param props Dashboard layout data and an optional wrapper class.
 * @returns A section element containing the resolved analytics widget grid.
 */
export const DashboardBuilder: Component<DashboardBuilderProps> = (props) => {
  const resolved = createMemo(
    (): SolidDashboardLayout =>
      Array.isArray(props.layout)
        ? { widgets: props.layout }
        : (props.layout as SolidDashboardLayout),
  );
  const [editableWidgets, setEditableWidgets] = createSignal<
    SolidDashboardWidget[]
  >(resolved().widgets);
  const [draggedWidgetId, setDraggedWidgetId] = createSignal<string | null>(
    null,
  );
  const [internalTimeRange, setInternalTimeRange] = createSignal(
    props.timeRange ?? props.timeRangeOptions?.[1]?.value ?? "7d",
  );
  const [internalSelectedWidgetId, setInternalSelectedWidgetId] = createSignal<
    string | null
  >(props.selectedWidgetId ?? null);
  const copy = createAnalyticsCopy();
  const timeRangeOptions = () =>
    props.timeRangeOptions ?? DEFAULT_TIME_RANGE_OPTIONS;
  const widgets = () =>
    props.editable ? editableWidgets() : resolved().widgets;
  const activeTimeRange = () => props.timeRange ?? internalTimeRange();
  const activeSelectedWidgetId = () =>
    props.selectedWidgetId ??
    internalSelectedWidgetId() ??
    widgets()[0]?.id ??
    null;
  const selectedWidget = () =>
    widgets().find((widget) => widget.id === activeSelectedWidgetId()) ?? null;

  createEffect(() => {
    setEditableWidgets(resolved().widgets);
  });

  createEffect(() => {
    if (props.timeRange !== undefined) {
      setInternalTimeRange(props.timeRange);
    }
  });

  createEffect(() => {
    if (props.selectedWidgetId !== undefined) {
      setInternalSelectedWidgetId(props.selectedWidgetId);
    }
  });

  const applyWidgetOrder = (nextWidgets: SolidDashboardWidget[]): void => {
    setEditableWidgets(nextWidgets);
    props.onLayoutChange?.(nextWidgets);
  };

  const handleMoveWidget = (widgetId: string, offset: number): void => {
    applyWidgetOrder(moveWidget(widgets(), widgetId, offset));
  };

  const handleSelectWidget = (widget: SolidDashboardWidget): void => {
    setInternalSelectedWidgetId(widget.id);
    props.onWidgetSelect?.(widget);
  };

  const handleTimeRangeChange = (nextTimeRange: string): void => {
    setInternalTimeRange(nextTimeRange);
    props.onTimeRangeChange?.(nextTimeRange);
  };

  return (
    <section
      class={cn("flex flex-col gap-4", props.class)}
      data-analytics-dashboard-builder
    >
      <Show when={resolved().title || resolved().description}>
        <header
          class="flex flex-col gap-1"
          data-analytics-dashboard-builder-header
        >
          <Show when={resolved().title}>
            <h2 class="text-lg font-semibold tracking-tight">
              {resolved().title}
            </h2>
          </Show>
          <Show when={resolved().description}>
            <p class="text-sm text-muted-foreground">
              {resolved().description}
            </p>
          </Show>
        </header>
      </Show>
      <Show when={props.editable}>
        <div
          class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3"
          data-analytics-dashboard-builder-toolbar
        >
          <label class="grid gap-1 text-xs font-medium text-muted-foreground">
            <span>{copy.t("analytics.dashboardBuilder.timeRangeAria")}</span>
            <select
              class="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring"
              value={activeTimeRange()}
              onChange={(event) =>
                handleTimeRangeChange(event.currentTarget.value)
              }
            >
              <For each={timeRangeOptions()}>
                {(option) => (
                  <option value={option.value}>{option.label}</option>
                )}
              </For>
            </select>
          </label>
          <span class="text-sm font-medium text-muted-foreground">
            {copy.plural(
              "analytics.dashboardBuilder.widgets",
              widgets().length,
            )}
          </span>
        </div>
      </Show>
      <div
        class="grid gap-4 md:grid-cols-2"
        data-analytics-dashboard-builder-grid
      >
        <For each={widgets()}>
          {(widget, index) => (
            <fieldset
              class={cn(
                "min-w-0 border-0 p-0",
                (widget.span ?? 1) > 1 && "md:col-span-2",
                props.editable &&
                  "rounded-lg border border-dashed border-border bg-background/60 p-3",
                activeSelectedWidgetId() === widget.id &&
                  "ring-2 ring-primary ring-offset-2",
              )}
              data-analytics-dashboard-builder-cell
              data-span={widget.span || 1}
              draggable={props.editable}
              onDragStart={() => setDraggedWidgetId(widget.id)}
              onDragOver={(event) => {
                if (props.editable) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                const draggedId = draggedWidgetId();
                if (draggedId) {
                  applyWidgetOrder(
                    moveWidgetToTarget(widgets(), draggedId, widget.id),
                  );
                }
                setDraggedWidgetId(null);
              }}
            >
              <legend class="sr-only">
                {copy.t("analytics.dashboardBuilder.widgetLegend", {
                  title: widget.title,
                })}
              </legend>
              <Show when={props.editable}>
                <div
                  class="mb-3 flex flex-wrap gap-2"
                  data-analytics-dashboard-widget-controls
                >
                  <button
                    type="button"
                    class="rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={index() === 0}
                    onClick={() => handleMoveWidget(widget.id, -1)}
                  >
                    {copy.t("analytics.dashboardBuilder.moveWidgetUp", {
                      title: widget.title,
                    })}
                  </button>
                  <button
                    type="button"
                    class="rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={index() === widgets().length - 1}
                    onClick={() => handleMoveWidget(widget.id, 1)}
                  >
                    {copy.t("analytics.dashboardBuilder.moveWidgetDown", {
                      title: widget.title,
                    })}
                  </button>
                  <button
                    type="button"
                    class="rounded-md border border-border px-2 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
                    onClick={() => handleSelectWidget(widget)}
                  >
                    {copy.t("analytics.dashboardBuilder.editWidget", {
                      title: widget.title,
                    })}
                  </button>
                </div>
              </Show>
              {renderWidget(widget, copy)}
            </fieldset>
          )}
        </For>
      </div>
      <Show when={props.editable && selectedWidget()}>
        {(widget) => (
          <aside
            class="rounded-lg border border-border bg-muted/30 p-4"
            data-analytics-dashboard-widget-editor
            role="complementary"
            aria-label={copy.t("analytics.dashboardBuilder.widgetEditor")}
          >
            <h3 class="text-sm font-semibold text-foreground">
              {copy.t("analytics.dashboardBuilder.widgetEditor")}
            </h3>
            <dl class="mt-3 grid gap-2 text-sm">
              <div class="grid gap-1">
                <dt class="text-xs font-medium uppercase text-muted-foreground">
                  {copy.t("analytics.dashboardBuilder.titleLabel")}
                </dt>
                <dd>{widget().title}</dd>
              </div>
              <div class="grid gap-1">
                <dt class="text-xs font-medium uppercase text-muted-foreground">
                  {copy.t("analytics.dashboardBuilder.kindLabel")}
                </dt>
                <dd>{widget().kind}</dd>
              </div>
              <div class="grid gap-1">
                <dt class="text-xs font-medium uppercase text-muted-foreground">
                  {copy.t("analytics.dashboardBuilder.spanLabel")}
                </dt>
                <dd>{widget().span ?? 1}</dd>
              </div>
              <Show when={widget().description}>
                <div class="grid gap-1">
                  <dt class="text-xs font-medium uppercase text-muted-foreground">
                    {copy.t("analytics.dashboardBuilder.descriptionLabel")}
                  </dt>
                  <dd>{widget().description}</dd>
                </div>
              </Show>
            </dl>
          </aside>
        )}
      </Show>
    </section>
  );
};
